import { GameEngine } from "~/engine/instance";

type GameUpdateCallback = (deltaTime: number) => void;

export type GameLoopConfig = {
  fixedUpdateFrequency?: number; // How many fixed updates per second
};

export class GameLoop {
  private lastFrameTime: number = 0;
  private accumulatedTime: number = 0;
  private fixedTimeStep: number;

  private updateCallbacks: GameUpdateCallback[] = [];
  private fixedUpdateCallbacks: GameUpdateCallback[] = [];

  constructor(config?: GameLoopConfig) {
    this.fixedTimeStep = 1000 / (config?.fixedUpdateFrequency || 60); // Default to 60 updates per second
  }

  private animationFrame: number | null = null;

  // track the average fps over the last 100 frames.
  // we track the index over the array to avoid reallocating the array
  private frameTimestampHistory: number[] = [];
  private frameTimestampHistoryIndex: number = 0;

  private startTime = 0;
  private pausedDelay = 0; // time that the game was paused for
  public gameTime = 0;

  // TODO: this appears like a dumb way to calculate fps? also slow probably
  public averageFPS(): number {
    // If the array is not full yet, we only want to consider non-zero values
    const validFrames = this.frameTimestampHistory.filter((t) => t > 0);

    if (validFrames.length <= 1) {
      return 0;
    }

    // Calculate the time differences between consecutive frames
    const frameTimes: number[] = [];

    // Sort the timestamps to ensure they're in chronological order
    const sortedFrames = [...validFrames].sort((a, b) => a - b);

    // Calculate time differences
    for (let i = 1; i < sortedFrames.length; i++) {
      const delta = sortedFrames[i]! - sortedFrames[i - 1]!;
      if (delta > 0) {
        // Avoid division by zero
        frameTimes.push(delta);
      }
    }

    if (frameTimes.length === 0) {
      return 0;
    }

    // Calculate average frame time in milliseconds
    const averageFrameTime =
      frameTimes.reduce((sum, time) => sum + time, 0) / frameTimes.length;

    // Convert to FPS (1000ms / frameTime)
    return 1000.0 / averageFrameTime;
  }

  private initLoop() {
    this.startTime = this.lastFrameTime = Math.floor(performance.now());
    this.frameTimestampHistory = Array.from<number>({ length: 100 }).fill(0);
    this.frameTimestampHistoryIndex = 0;
    this.animationFrame = requestAnimationFrame(this.loop);
  }

  /**
   * Start the game loop
   */
  public start(): void {
    this.pausedDelay = 0;
    this.gameTime = 0;

    const state = GameEngine.gameStore.getState();
    if (!this.shouldBePaused(state)) {
      this.initLoop();
    }

    const unsubscribe = GameEngine.gameStore.subscribe(
      (nextState, prevState) => {
        const wasPaused = this.shouldBePaused(prevState);
        const isPaused = this.shouldBePaused(nextState);

        if (isPaused !== wasPaused) {
          if (isPaused) {
            this.cancelAnimationFrame();
          } else {
            this.pausedDelay += GameEngine.gameLoop.gameTime;
            this.initLoop();
          }
        }
      },
    );
    this.unsubscribers.push(unsubscribe);
  }
  private unsubscribers: (() => void)[] = [];

  private cancelAnimationFrame(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Check if the game should be paused (any menu is open)
   */
  private shouldBePaused(state = GameEngine.gameStore.getState()): boolean {
    return state.openMenu !== null;
  }

  /**
   * Register a callback to be called every frame with variable delta time
   */
  public onUpdate(callback: GameUpdateCallback): void {
    this.updateCallbacks.push(callback);
  }

  /**
   * Register a callback to be called at a fixed time interval
   */
  public onFixedUpdate(callback: GameUpdateCallback): void {
    this.fixedUpdateCallbacks.push(callback);
  }

  /**
   * Main loop function called every frame
   */
  private loop = (timestamp: number): void => {
    if (this.shouldBePaused()) return;

    // Calculate delta time in milliseconds
    const deltaTime = timestamp - this.lastFrameTime;
    this.lastFrameTime = timestamp;

    this.gameTime = timestamp - this.startTime + this.pausedDelay;

    // Prevent spiral of death with large delta times
    const clampedDeltaTime = Math.min(deltaTime, 100);

    // Accumulate time for fixed updates
    this.accumulatedTime += clampedDeltaTime;

    // Process all variable updates
    this.processUpdates(clampedDeltaTime / 1000); // Convert to seconds

    // Process fixed time step updates
    while (this.accumulatedTime >= this.fixedTimeStep) {
      this.frameTimestampHistory[this.frameTimestampHistoryIndex] = timestamp;
      this.frameTimestampHistoryIndex =
        (this.frameTimestampHistoryIndex + 1) %
        this.frameTimestampHistory.length;

      this.processFixedUpdates(this.fixedTimeStep / 1000); // Convert to seconds
      this.accumulatedTime -= this.fixedTimeStep;
    }

    // Schedule next frame
    this.animationFrame = requestAnimationFrame(this.loop);
  };

  /**
   * Process all update callbacks
   */
  private processUpdates(deltaTime: number): void {
    for (const callback of this.updateCallbacks) {
      callback(deltaTime);
    }
  }

  /**
   * Process all fixed update callbacks
   */
  private processFixedUpdates(fixedDeltaTime: number): void {
    for (const callback of this.fixedUpdateCallbacks) {
      callback(fixedDeltaTime);
    }
  }

  public dispose(): void {
    this.cancelAnimationFrame();

    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers.length = 0;
  }
}
